/**
 * Landing bloklari uchun publik ma'lumot hooklari.
 *
 * Qoida `shared/data/portal.ts` dagi bilan bir xil: backend javobi ustun,
 * aloqa bo'lmasa — zaxira kontent (`shared/data/landing.ts`). Har bir so'rov
 * til bo'yicha modul darajasida keshlanadi: bloklar qayta chizilganda
 * takroriy so'rov yuborilmaydi.
 */
import { useEffect, useState } from "react";

import { portalApi } from "@/shared/api/endpoints";
import type {
  PortalAbout,
  PortalGuide,
  PortalMediaAsset,
  PublicMapConfig,
  PublicMapFilters,
} from "@/shared/api/types";
import { useI18n } from "@/shared/i18n";

type Loader<T> = (lang: string) => Promise<T>;

const caches = new Map<string, Promise<unknown>>();

function cached<T>(key: string, loader: () => Promise<T>): Promise<T> {
  let request = caches.get(key) as Promise<T> | undefined;
  if (!request) {
    request = loader();
    request.catch(() => caches.delete(key));
    caches.set(key, request);
  }
  return request;
}

export interface PublicState<T> {
  data: T | null;
  /** Server javob berdi (ma'lumot bo'sh bo'lishi ham mumkin). */
  loaded: boolean;
  /** Aloqa yo'q — zaxira kontent ko'rsatiladi. */
  offline: boolean;
}

function usePublic<T>(name: string, loader: Loader<T>, languageAware = true): PublicState<T> {
  const { lang } = useI18n();
  const key = languageAware ? `${name}:${lang}` : name;
  const [state, setState] = useState<PublicState<T>>({ data: null, loaded: false, offline: false });

  useEffect(() => {
    let cancelled = false;
    cached(key, () => loader(lang))
      .then((data) => {
        if (!cancelled) setState({ data, loaded: true, offline: false });
      })
      .catch(() => {
        if (!cancelled) setState({ data: null, loaded: false, offline: true });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}

export const usePortalAbout = () => usePublic<PortalAbout>("about", portalApi.about);
export const usePortalGuides = () => usePublic<PortalGuide[]>("guides", portalApi.guides);
export const usePortalMedia = () => usePublic<PortalMediaAsset[]>("media", portalApi.media);
export const usePublicMap = () => usePublic<PublicMapConfig>("map", portalApi.map);
export const usePublicMapFilters = () =>
  usePublic<PublicMapFilters>("map-filters", () => portalApi.mapFilters(), false);

/* ------------------------------------------------------------ formatlash */

const COMPACT: Record<string, Array<[number, string]>> = {
  uz: [[1e9, "mlrd"], [1e6, "mln"]],
  ru: [[1e9, "млрд"], [1e6, "млн"]],
  en: [[1e9, "bn"], [1e6, "M"]],
};

/**
 * Statistik qiymatni `CountUp` tushunadigan ko'rinishga keltiradi: guruhlar
 * probel bilan, kasr — vergul bilan (masalan «11,2 mln», «24,9», «210»).
 */
export function formatStat(value: number | null, lang: string): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  const steps = COMPACT[lang] ?? COMPACT.uz;
  for (const [step, suffix] of steps) {
    if (Math.abs(value) >= step) {
      const scaled = (value / step).toFixed(1).replace(/\.0$/, "").replace(".", ",");
      return `${scaled} ${suffix}`;
    }
  }
  const decimals = Number.isInteger(value) ? 0 : Math.abs(value) < 10 ? 2 : 1;
  const [whole, fraction] = value.toFixed(decimals).split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return fraction ? `${grouped},${fraction}` : grouped;
}
